import type { Middleware } from '@reduxjs/toolkit';
import { parseNavHidden } from '../../config/navItems';
import { has } from 'lodash-es';
import { getSocket, updateSocketToken, emitWithAck,
  GET_AGENT_RECORDS, GET_MY_AGENT_KEY, GET_AGENT_LOCATIONS,
  GET_USER_UI_SETTINGS, GET_DELIVERY_TODAY, GET_CALL_TODAY,
  GET_MENU_ITEM, GET_MENU_GROUP, GET_MENU_ITEM_GROUP_COMPLEMENT,
  GET_MENU_GROUP_GROUP_COMPLEMENT, GET_MENU_ITEM_GROUP_MEMBER,
  GET_LOCATION, GET_DELIVERYMAN, GET_CHECKIN,
  PUSH_DELIVERY, PUSH_CALL, PUSH_LOCATION, PUSH_MENU_ITEM,
  PUSH_MENU_GROUP, PUSH_MENU_ITEM_GROUP_MEMBER, PUSH_MENU_GROUP_GROUP_COMPLEMENT,
  PUSH_MENU_ITEM_GROUP_COMPLEMENT, PUSH_DELIVERYMAN, PUSH_CHECKIN,
  DELETE_MENU_GROUP_GROUP_COMPLEMENT, DELETE_MENU_ITEM_GROUP_COMPLEMENT,
  DELETE_MENU_ITEM_GROUP_MEMBER, DELETE_DELIVERYMAN, DELETE_LOCATION,
  FETCH_CUSTOMER_DATA, GET_CALL_DATE, GET_DELIVERY_DATE, GET_ADDRESS_DISTANCE,
  POST_DELIVERY, PUSH_PRINTER_STATE, PUSH_ONLINE_ORDERS_PAUSED, PUSH_MIN_DELIVERY_TIME,
  PUSH_RESERVATION,
  PUSH_TABLE_SESSION, PUSH_FLOORPLAN_TABLE, PUSH_FLOORPLAN_ZONE,
  DELETE_FLOORPLAN_TABLE, DELETE_FLOORPLAN_ZONE, DELETE_TABLE_SESSION,
} from '../../services/socket';
import { decodeToken } from '../../utils/auth';
import { loginUser, loginUserSuccess, logout, wsConnected, wsDisconnected } from '../slices/authSlice';
import { setCallsToday, setCallsForDate, pushCall } from '../slices/callSlice';
import { setDeliveriesToday, setDeliveriesForDate, pushDelivery } from '../slices/deliverySlice';
import { setMenuItems, pushMenuItem, setMenuGroups, pushMenuGroup,
  setGroupMembers, pushGroupMember, deleteGroupMember,
  setGroupGroupComplements, pushGroupGroupComplement, deleteGroupGroupComplement,
  setItemGroupComplements, pushItemGroupComplement, deleteItemGroupComplement,
} from '../slices/menuSlice';
import { setLocations, pushLocation, deleteLocation } from '../slices/locationSlice';
import { setDeliverymen, pushDeliveryman, deleteDeliveryman } from '../slices/deliverymanSlice';
import { setCheckins, pushCheckin } from '../slices/checkinSlice';
import { setAgents, setAgentLocations } from '../slices/agentSlice';
import { setPrinterStates, type PrinterState } from '../slices/printerSlice';
import { setVisibleComponents, setActiveAgent, setLoadedCall, setLoadedCustomer,
  setCustomerAddressCount, openModalCustomerDataLoad, closeModalCustomerDataLoad,
  loadPendingCallData, resetCustomer,
  setRecordsDate, setDeliveryAddressDistance,
  setDiscountLevels,
  setLocalCountryPrefix,
  setCurrency,
  setDeliveryModulo,
  setDeliveryColumns,
  setUserId,
  setOnlineOrdersPaused,
  setMinDeliveryTime,
  setShowMinDeliveryTime,
  setReservationsEnabled,
  setIvrNewUi,
  setNavHidden,
  setShowDeliverymanStats,
  setAllowNonAdminAddDeliverymen,
  setShowScannerIndicator,
  setWoltEnabled,
  setPendingReservationCount,
} from '../slices/uiSlice';
import { checkout, resetCart } from '../slices/cartSlice';
import {
  setFloorplan, pushFloorplanTable, deleteFloorplanTable,
  pushZone, deleteZone, pushTableSession, markSessionClosed,
} from '../slices/tableSlice';
import { fetchBusinessById, fetchPrinterState, getUserPreferences, fetchReservations, fetchFloorplan } from '../../services/api';
import type { RootState } from '../index';
import {
  decideIncomingCall,
  shouldDropPendingOnPush,
  shouldUpdateLoadedCallOnPush,
  type OrderFormSnapshot,
} from './callOrchestration';

let socketInitialized = false;
let activeAgentKey: string | null = null;
let activeUserPublicId: string | null = null;
let pendingCallData: any = null;

function orderSnapshot(state: RootState): OrderFormSnapshot {
  return {
    loadedCustomer: state.ui.loadedCustomer,
    cartItemCount: Object.keys(state.cart.cartItems || {}).length,
    orderFormFields: state.ui.orderFormFields as OrderFormSnapshot['orderFormFields'],
  };
}

/**
 * This middleware handles:
 * 1. Socket connection lifecycle (on login/logout)
 * 2. Socket event listeners setup
 * 3. Socket emit actions (delivery creation, date changes, etc.)
 */
export const socketMiddleware: Middleware = (storeApi) => {
  const { dispatch, getState } = storeApi;

  function initSocketListeners() {
    if (socketInitialized) return;
    socketInitialized = true;

    const socket = getSocket();

    // ===== Connection events =====
    socket.on('connect', () => {
      dispatch(wsConnected());
      const token = localStorage.getItem('token');
      if (!token) return;

      const publicId = decodeToken(token).public_id;

      // Fetch UI settings
      socket.emit(GET_USER_UI_SETTINGS, publicId, (data: any) => {
        if (data) dispatch(setVisibleComponents(data));
      });

      // Fetch business discount levels
      fetchBusinessById(1).then((res) => {
        const levels = res.data?.cfg_discount_levels;
        if (levels && typeof levels === 'string' && levels.trim()) {
          const parsed = [0, ...levels.split(',').map(Number).filter((n) => !isNaN(n) && n > 0)];
          dispatch(setDiscountLevels(parsed));
        }
        dispatch(setLocalCountryPrefix(res.data?.cfg_local_country_prefix || ''));
        dispatch(setCurrency(res.data?.cfg_currency || 'EUR'));
        dispatch(setDeliveryModulo(res.data?.cfg_delivery_modulo || null));
        dispatch(setOnlineOrdersPaused(res.data?.cfg_online_orders_paused === true));
        dispatch(setMinDeliveryTime(res.data?.cfg_min_delivery_time ?? null));
        dispatch(setShowMinDeliveryTime(res.data?.cfg_show_min_delivery_time === true));
        dispatch(setReservationsEnabled(res.data?.cfg_reservations_enabled === true));
        dispatch(setIvrNewUi(res.data?.cfg_ivr_new_ui === true));
        dispatch(setNavHidden(parseNavHidden(res.data?.cfg_nav_hidden)));
        dispatch(setShowDeliverymanStats(res.data?.cfg_show_deliveryman_stats === true));
        dispatch(setAllowNonAdminAddDeliverymen(res.data?.cfg_allow_non_admin_add_deliverymen === true));
        dispatch(setShowScannerIndicator(res.data?.cfg_show_scanner_indicator === true));
        dispatch(setWoltEnabled(res.data?.cfg_wolt_enabled === true));
      }).catch(() => { /* ignore — use defaults */ });

      // Fetch user preferences (delivery column config)
      getUserPreferences().then((res) => {
        if (res.data?.id) dispatch(setUserId(res.data.id));
        if (res.data?.ui_delivery_columns) {
          dispatch(setDeliveryColumns(res.data.ui_delivery_columns));
        }
      }).catch(() => { /* ignore */ });

      // Fetch pending reservations count (today + future)
      const todayStr = new Date().toISOString().split('T')[0];
      const futureStr = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      fetchReservations(todayStr, futureStr).then((res) => {
        const pending = (res.data || []).filter((r) => r.approved == null).length;
        dispatch(setPendingReservationCount(pending));
      }).catch(() => { /* ignore */ });

      // Fetch agents
      socket.emit(GET_AGENT_RECORDS, (data: any) => {
        if (data) dispatch(setAgents(data));
      });

      // Fetch my agent key
      socket.emit(GET_MY_AGENT_KEY, publicId, (data: any) => {
        if (activeAgentKey) {
          deactivateAgentListener(activeAgentKey);
        }
        if (has(data, 'agent_key')) {
          localStorage.setItem('agent_key', data.agent_key);
          activateAgentListener(data.agent_key);
          // Fetch agent locations
          socket.emit(GET_AGENT_LOCATIONS, data.agent_key, (locData: any) => {
            if (locData) dispatch(setAgentLocations(JSON.parse(locData.payload)));
          });
        }
      });

      // Fetch today's data
      socket.emit(GET_DELIVERY_TODAY, (data: any) => {
        if (data?.payload) {
          dispatch(setDeliveriesToday(JSON.parse(data.payload)));
        }
      });
      socket.emit(GET_CALL_TODAY, (data: any) => {
        if (data?.payload) dispatch(setCallsToday(data.payload));
      });
      socket.emit(GET_MENU_ITEM, (data: any) => {
        if (data?.payload) {
          const parsed = typeof data.payload === 'string' ? JSON.parse(data.payload) : data.payload;
          dispatch(setMenuItems(parsed));
        }
      });
      socket.emit(GET_MENU_GROUP, (data: any) => {
        if (data?.payload) dispatch(setMenuGroups(data.payload));
      });
      socket.emit(GET_MENU_ITEM_GROUP_COMPLEMENT, (data: any) => {
        if (data?.payload) dispatch(setItemGroupComplements(data.payload));
      });
      socket.emit(GET_MENU_GROUP_GROUP_COMPLEMENT, (data: any) => {
        if (data?.payload) dispatch(setGroupGroupComplements(data.payload));
      });
      socket.emit(GET_MENU_ITEM_GROUP_MEMBER, (data: any) => {
        if (data?.payload) dispatch(setGroupMembers(data.payload));
      });
      socket.emit(GET_LOCATION, (data: any) => {
        if (data?.payload) dispatch(setLocations(JSON.parse(data.payload)));
      });
      socket.emit(GET_DELIVERYMAN, (data: any) => {
        if (data?.payload) dispatch(setDeliverymen(data.payload));
      });
      socket.emit(GET_CHECKIN, (data: any) => {
        if (data?.payload) dispatch(setCheckins(data.payload));
      });

      // Seed printer indicators with current state from backend
      fetchPrinterState()
        .then((res) => {
          if (Array.isArray(res.data)) dispatch(setPrinterStates(res.data));
        })
        .catch(() => { /* indicators just stay empty */ });

      // Seed the restaurant floor plan (zones + tables w/ live occupancy).
      // Subsequent changes arrive via /PUSH/floorplan_* and /PUSH/table_session.
      fetchFloorplan()
        .then((res) => { if (res.data) dispatch(setFloorplan(res.data)); })
        .catch(() => { /* no tables configured — floor plan just stays empty */ });
    });

    socket.on('reconnect', () => {
      dispatch(wsConnected());
    });

    socket.on('disconnect', () => {
      dispatch(wsDisconnected());
    });

    // ===== Push listeners =====
    socket.on(PUSH_MENU_ITEM, (payload: string) => dispatch(pushMenuItem(JSON.parse(payload))));
    socket.on(PUSH_MENU_GROUP, (payload: any) => dispatch(pushMenuGroup(payload)));
    socket.on(PUSH_MENU_ITEM_GROUP_MEMBER, (payload: any) => dispatch(pushGroupMember(payload)));
    socket.on(DELETE_MENU_ITEM_GROUP_MEMBER, (payload: any) => dispatch(deleteGroupMember(payload)));
    socket.on(PUSH_MENU_GROUP_GROUP_COMPLEMENT, (payload: any) => dispatch(pushGroupGroupComplement(payload)));
    socket.on(DELETE_MENU_GROUP_GROUP_COMPLEMENT, (payload: any) => dispatch(deleteGroupGroupComplement(payload)));
    socket.on(PUSH_MENU_ITEM_GROUP_COMPLEMENT, (payload: any) => dispatch(pushItemGroupComplement(payload)));
    socket.on(DELETE_MENU_ITEM_GROUP_COMPLEMENT, (payload: any) => dispatch(deleteItemGroupComplement(payload)));
    socket.on(PUSH_DELIVERY, (payload: any) => {
      try {
        const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
        dispatch(pushDelivery(parsed));
      } catch (e) {
        // silently ignore malformed delivery pushes
      }
    });
    socket.on(PUSH_CALL, (payload: any) => {
      dispatch(pushCall(payload));
      const state = getState();
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
      // Sync loadedCall with the updated payload (so finished_at propagates).
      // DO NOT null loadedCall on finish — the operator may still be editing
      // the order, and submit must attach call.id to link the delivery back
      // to its originating call.
      if (shouldUpdateLoadedCallOnPush(state.ui.loadedCall?.uuid, parsed)) {
        dispatch(setLoadedCall(parsed));
      }
      if (shouldDropPendingOnPush(pendingCallData?.CALL?.uuid, parsed)) {
        pendingCallData = null;
        if (state.ui.modalCustomerDataLoad) {
          dispatch(closeModalCustomerDataLoad());
        }
      }
    });
    socket.on(PUSH_LOCATION, (payload: string) => dispatch(pushLocation(JSON.parse(payload))));
    socket.on(PUSH_CHECKIN, (payload: any) => dispatch(pushCheckin(payload)));
    socket.on(PUSH_DELIVERYMAN, (payload: any) => dispatch(pushDeliveryman(payload)));
    socket.on(DELETE_DELIVERYMAN, (payload: any) => dispatch(deleteDeliveryman(payload)));
    socket.on(DELETE_LOCATION, (payload: any) => dispatch(deleteLocation(payload)));

    // Online orders paused state broadcast
    socket.on(PUSH_ONLINE_ORDERS_PAUSED, (payload: { paused: boolean }) => {
      dispatch(setOnlineOrdersPaused(payload.paused));
    });

    // Min delivery time broadcast
    socket.on(PUSH_MIN_DELIVERY_TIME, (payload: { minutes: number | null; show: boolean }) => {
      dispatch(setMinDeliveryTime(payload.minutes));
      dispatch(setShowMinDeliveryTime(payload.show));
    });

    // Reservation push: refetch pending count
    socket.on(PUSH_RESERVATION, () => {
      const todayStr = new Date().toISOString().split('T')[0];
      const futureStr = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      fetchReservations(todayStr, futureStr).then((res) => {
        const pending = (res.data || []).filter((r) => r.approved == null).length;
        dispatch(setPendingReservationCount(pending));
      }).catch(() => { /* ignore */ });
    });

    // Table ordering live sync (payloads are JSON objects, not strings)
    socket.on(PUSH_TABLE_SESSION, (payload: any) => dispatch(pushTableSession(payload)));
    socket.on(PUSH_FLOORPLAN_TABLE, (payload: any) => dispatch(pushFloorplanTable(payload)));
    socket.on(PUSH_FLOORPLAN_ZONE, (payload: any) => dispatch(pushZone(payload)));
    socket.on(DELETE_FLOORPLAN_TABLE, (payload: any) => dispatch(deleteFloorplanTable(payload)));
    socket.on(DELETE_FLOORPLAN_ZONE, (payload: any) => dispatch(deleteZone(payload)));
    socket.on(DELETE_TABLE_SESSION, (payload: any) => dispatch(markSessionClosed(payload)));

    // Printer indicators (zebraman → backend Redis bridge → here)
    socket.on(PUSH_PRINTER_STATE, (payload: PrinterState[] | string) => {
      try {
        const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
        if (Array.isArray(parsed)) dispatch(setPrinterStates(parsed));
      } catch {
        /* ignore malformed printer state pushes */
      }
    });
  }

  function activateAgentListener(agentKey: string) {
    activeAgentKey = agentKey;
    dispatch(setActiveAgent(agentKey));
    const socket = getSocket();
    socket.on(agentKey, (data: any) => {
      if (has(data, 'CALL')) {
        const decision = decideIncomingCall(orderSnapshot(getState() as RootState));
        if (decision === 'load-immediately') {
          loadActiveCallData(data);
        } else {
          pendingCallData = data;
          dispatch(openModalCustomerDataLoad());
        }
      }
    });
  }

  function deactivateAgentListener(agentKey: string) {
    const socket = getSocket();
    socket.off(agentKey);
    activeAgentKey = null;
  }

  function loadActiveCallData(phonecallData: any) {
    // Only treat CUSTOMER as "existing customer" when the backend actually
    // found one. Unknown callers now come through as null (or an empty
    // object from older backends) — in those cases we leave loadedCustomer
    // untouched so the form stays blank for the operator to fill in.
    if (has(phonecallData, 'CUSTOMER') && phonecallData.CUSTOMER && phonecallData.CUSTOMER.id) {
      const customerData = phonecallData.CUSTOMER;
      dispatch(setLoadedCustomer(customerData));
      if (customerData.addresses) {
        dispatch(setCustomerAddressCount(customerData.addresses.length));
      }
    }
    if (has(phonecallData, 'CALL')) {
      dispatch(setLoadedCall(phonecallData.CALL));
    }
  }

  return (next) => (action) => {
    // Handle login - connect socket
    if (loginUserSuccess.match(action)) {
      const token = action.payload.token || action.payload;
      updateSocketToken(token);
      initSocketListeners();
      const socket = getSocket();
      socket.open();

      const publicId = decodeToken(token).public_id;
      activeUserPublicId = publicId;

      // Listen for user-specific events
      socket.on(publicId, () => {
        socket.emit(GET_MY_AGENT_KEY, publicId, (data: any) => {
          if (activeAgentKey) deactivateAgentListener(activeAgentKey);
          if (has(data, 'agent_key')) {
            localStorage.setItem('agent_key', data.agent_key);
            activateAgentListener(data.agent_key);
          }
        });
      });
    }

    // Handle logout - disconnect socket
    if (logout.match(action)) {
      const socket = getSocket();
      if (activeUserPublicId) socket.off(activeUserPublicId);
      if (activeAgentKey) deactivateAgentListener(activeAgentKey);
      socket.close();
      activeUserPublicId = null;
    }

    // Fetch both deliveries and calls when date changes
    if (setRecordsDate.match(action)) {
      const result = next(action);
      const socket = getSocket();
      socket.emit(GET_DELIVERY_DATE, action.payload, (data: any) => {
        if (data?.payload) {
          dispatch(setDeliveriesForDate(JSON.parse(data.payload)));
        }
      });
      socket.emit(GET_CALL_DATE, action.payload, (data: any) => {
        if (data?.payload) dispatch(setCallsForDate(data.payload));
      });
      return result;
    }

    // Load pending call data when user chooses "Nova narudžba"
    if (loadPendingCallData.match(action)) {
      const result = next(action);
      if (pendingCallData) {
        const data = pendingCallData;
        pendingCallData = null;
        dispatch(resetCart());
        loadActiveCallData(data);
      }
      return result;
    }


    return next(action);
  };
};
